import React, {
  useCallback,
  useEffect,
  useState
} from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import {
  useDrag,
  useDrop
} from "react-dnd";
import type {
  XYCoord,
  Identifier
} from "dnd-core";

import { DateInput } from "src/core/shared/components/Date/DateInput";
import { Text } from "src/library/components/Text";
import { ActionButton } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

import { IProduct } from "../api/products.api";
import { ItemFlexFigures } from "./DraggableDatedProducts";

export enum UpdateDateType {
  Start = "startDate",
  End = "endDate"
}

export const ItemTypes = { CARD: "card" };

interface DragItem {
  index: number;
  type: string;
}

export interface UpdateProductListParams {
  date: Date,
  productId: number,
  updateDateType: UpdateDateType
}

export interface DraggableDatedProduct {
  productId: number;
  startDate: Date;
  endDate?: Date | null;
  priority?: number;
  product?: IProduct;
}

export interface DraggableDatedProductsRowProps {
  priority: number;
  datedProduct: DraggableDatedProduct;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  updateList: (data: UpdateProductListParams) => void;
  handleDeleteRow: (productId: number) => void;
  lockSortableFeature?: boolean;
  disableInputs?: boolean;
}

/**
 * Renders a draggable row displaying dated product data
 */
const DraggableDatedProductsRow: React.ReactFCWithChildren<DraggableDatedProductsRowProps> = ({
  priority, datedProduct, moveCard, updateList, handleDeleteRow, lockSortableFeature = false, disableInputs = false
}) => {
  const [ startDate, setStartDate ] = useState(moment.utc(datedProduct.startDate).toDate());
  const [ endDate, setEndDate ] = useState(datedProduct.endDate ? moment.utc(datedProduct.endDate).toDate() : undefined);
  const [ productId, setProductId ] = useState(datedProduct.productId);
  const ref = React.useRef<HTMLDivElement>(null);
  const previewRef = React.useRef<HTMLDivElement>(null);

  // stop start date from being set after end date
  const updateStartDate = useCallback((date: Date, productId: number) => {
    if (endDate && moment.utc(date).isSameOrAfter(moment.utc(endDate))) {
      toast.error("Start date can not be after end date.");

      return;
    }

    updateList({
      date,
      productId,
      updateDateType: UpdateDateType.Start
    });
    setStartDate(date);
  }, [ endDate, updateList ]);

  // update the products list on endDate change
  const updateEndDate = useCallback((date: Date, productId: number) => {
    updateList({
      date,
      productId,
      updateDateType: UpdateDateType.End
    });
    setEndDate(date);
  }, [ updateList ]);

  // ensure productId
  useEffect(() => {
    if (datedProduct.productId !== productId) {
      setProductId(productId);
    }
  }, [ productId, datedProduct ]);

  // draggable logic
  const [ { handlerId }, drop ] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = priority;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });

  const [
    { isDragging },
    drag,
    dragPreview
  ] = useDrag({
    type: ItemTypes.CARD,
    item: () => ({ index: priority }),
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));
  dragPreview(drop(previewRef));

  // don't return row if product data not included
  if (!datedProduct.product) {
    return null;
  }

  return (
    <RowWrapper>
      <div
        className="row"
        ref={previewRef}
        data-handler-id={handlerId}
        style={{ opacity }}
      >
        {!lockSortableFeature && (
          <div
            ref={ref}
            className="drag-icon-wrapper"
          >
            <Icon
              icon={Icons.DRAG}
              size={15}
            />
          </div>
        )}

        <div className="details">
          <div className="item">
            <Text>
              {datedProduct.product.description}
            </Text>
          </div>

          <div className="item">
            <Text>
              {datedProduct.product.partNumber}
            </Text>
          </div>

          <div className="item item-date">
            <DateInput
              label="Start Date"
              date={moment.utc(startDate).toDate()}
              setDate={date => updateStartDate(date, productId)}
              minimumDate={new Date()}
              disabled={disableInputs}
            />
          </div>

          <div className="item item-date">
            <DateInput
              label="End Date"
              date={endDate ? moment.utc(endDate).toDate() : undefined}
              setDate={date => updateEndDate(date, productId)}
              minimumDate={startDate ? moment.utc(startDate).toDate() : new Date()}
              maximumDate={moment.utc().add(2, "years").toDate()}
              disabled={disableInputs}
            />
          </div>

          <div className="item actions">
            {!disableInputs && (
              <ActionButton
                title="actions"
                items={[
                  {
                    title: "Delete",
                    action: () => handleDeleteRow(datedProduct.productId)
                  }
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </RowWrapper>
  );
};

const RowWrapper = styled.div`
  ${({ theme }) => css`
  margin-right: ${theme.spacing.padding.lg};

  .row {
    display: flex;
    align-items: center;
  }
  
  .drag-icon-wrapper {          
    svg {
      margin-right: ${theme.spacing.margins.md};
      
      :hover {
        cursor: grab;
      }
    }
  }
  
  .details {
    background: ${theme.colors.white};
    border-bottom: ${theme.colors.lightGrey} 1px solid;
    min-width: ${ItemFlexFigures.minWidth};
    display: flex;
    align-items: center;
    flex-flow: row wrap;
  }

  .item {
    flex-basis: ${ItemFlexFigures.flexBasis};
    flex-grow: ${ItemFlexFigures.flexGrow};
    padding: ${theme.spacing.padding.sm};
  }

  .actions {
    display: flex;
    justify-content: end;
  }
  `}
`;

export default DraggableDatedProductsRow;
